import { useState, ReactNode, useEffect, FormEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Container from "components/__reusable/Container";
import PasswordInput from "components/__reusable/PasswordInput";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import type { ChangePasswordFormData } from "types/User";
import { changePassword } from "services/userServices";
import LoadingDialog from "components/__reusable/LoadingDialog";
import { getCurrentLanguage } from "utilities/CurrentLanguageGetter";
import useAppDispatch from "hooks/useAppDispatch";
import { setSuccessStatus } from "features/success/successSlice";
import { setErrorStatus } from "features/error/errorSlice";

interface ValidationErrors {
  oldPassword?: string;
  newPassword?: string;
  confirmedNewPassword?: string;
}

const Form = () => {
  const { t } = useTranslation();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmedNewPassword, setConfirmedNewPassword] = useState("");

  const [touchedFields, setTouchedFields] = useState({
    oldPassword: false,
    newPassword: false,
    confirmedNewPassword: false,
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  // --- Password Validation Function (prioritized single error) ---
  const validatePassword = (pwd: string): string => {
    // Priority 1: Minimum Length
    if (pwd.length < 8) {
      return "このパスワードは短すぎます。最低 8 文字以上必要です。";
    }

    // Priority 2: Uppercase Letter
    if (!/[A-Z]/.test(pwd)) {
      return "パスワードには大文字を1文字以上含めてください";
    }

    // Priority 3: Lowercase Letter
    if (!/[a-z]/.test(pwd)) {
      return "パスワードには小文字を1文字以上含めてください";
    }

    // Priority 4: Number
    if (!/[0-9]/.test(pwd)) {
      return "パスワードには数字を1文字以上含めてください";
    }

    return "";
  };

  // --- Reusable Input Renderer ---
  const renderInput = (label: string, inputElement: ReactNode) => (
    <div className="lg:flex items-center space-y-1 lg:space-x-3 lg:space-y-0">
      <h3 className="lg:w-1/4 font-medium">{label}</h3>
      <div className="lg:w-3/4">{inputElement}</div>
    </div>
  );

  // --- Centralized Validation Logic ---
  const validateAllFields = useCallback(() => {
    const errors: ValidationErrors = {};

    // 1. Current Password Validation
    if (!oldPassword.trim()) {
      errors.oldPassword = t("_change_password._current_password_required");
    }

    // 2. New Password Validation
    if (!newPassword.trim()) {
      errors.newPassword = t("_change_password._new_password_required");
    } else {
      const passwordError = validatePassword(newPassword);

      if (passwordError) {
        errors.newPassword = passwordError;
      } else if (newPassword === oldPassword) {
        // Check if new password is same as old password (after format validation passes)
        errors.newPassword = t("_change_password._same_as_old_password");
      }
    }

    // 3. Confirmed New Password Validation
    if (!confirmedNewPassword.trim()) {
      errors.confirmedNewPassword = t(
        "_change_password._confirm_password_required"
      );
    } else if (confirmedNewPassword !== newPassword) {
      errors.confirmedNewPassword = t(
        "_change_password._passwords_do_not_match"
      );
    }

    return errors;
  }, [t, oldPassword, newPassword, confirmedNewPassword]);

  // --- Single useEffect for all real-time validation ---
  useEffect(() => {
    const allErrors = validateAllFields();
    const currentErrors: ValidationErrors = {};

    // Only show errors for fields that the user has interacted with (touched)
    if (touchedFields.oldPassword && allErrors.oldPassword) {
      currentErrors.oldPassword = allErrors.oldPassword;
    }
    if (touchedFields.newPassword && allErrors.newPassword) {
      currentErrors.newPassword = allErrors.newPassword;
    }
    if (touchedFields.confirmedNewPassword && allErrors.confirmedNewPassword) {
      currentErrors.confirmedNewPassword = allErrors.confirmedNewPassword;
    }

    setValidationErrors(currentErrors);

    // Disable button if there are any errors or if fields are empty
    const hasEmptyFields =
      !oldPassword.trim() ||
      !newPassword.trim() ||
      !confirmedNewPassword.trim();

    const hasErrors = Object.values(allErrors).some(
      (error) => error && error.trim().length > 0
    );

    setIsDisabled(hasEmptyFields || hasErrors);
  }, [
    oldPassword,
    newPassword,
    confirmedNewPassword,
    touchedFields,
    validateAllFields,
  ]);

  const handleFieldBlur = (fieldName: keyof typeof touchedFields) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched to display any final errors
    setTouchedFields({
      oldPassword: true,
      newPassword: true,
      confirmedNewPassword: true,
    });

    const errors = validateAllFields();
    setValidationErrors(errors);

    // Final check before submission
    const hasAnyErrors = Object.values(errors).some(
      (error) => error && error.trim().length > 0
    );

    if (hasAnyErrors) {
      return;
    }

    setIsLoading(true);
    try {
      const formData: ChangePasswordFormData = {
        old_password: oldPassword,
        new_password: newPassword,
        verify_password: confirmedNewPassword,
      };

      await changePassword(formData, getCurrentLanguage());

      dispatch(
        setSuccessStatus({
          title: "_change_password._change_complete",
          message: "_change_password._change_complete_content",
        })
      );

      // Reset form
      setOldPassword("");
      setNewPassword("");
      setConfirmedNewPassword("");
      setTouchedFields({
        oldPassword: false,
        newPassword: false,
        confirmedNewPassword: false,
      });
    } catch (error: any) {
      dispatch(
        setErrorStatus({
          title: "入力エラー",
          message: "現在のパスワードが正しくありません。",
          buttonStyle: "red",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      {isLoading && <LoadingDialog />}
      <form className="space-y-3 lg:space-y-5" onSubmit={handleSubmit}>
        {renderInput(
          `${t("_change_password._current_password")}`,
          <div>
            <PasswordInput
              name="old_password"
              placeholder={t("_change_password._current_password")}
              value={oldPassword}
              disabled={false}
              onChange={(value) => setOldPassword(value)}
              onBlur={() => handleFieldBlur("oldPassword")}
            />
            {validationErrors.oldPassword && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.oldPassword}
              </p>
            )}
          </div>
        )}

        {renderInput(
          `${t("_change_password._new_password")}`,
          <div>
            <PasswordInput
              name="new_password"
              placeholder={t("_change_password._new_password")}
              value={newPassword}
              disabled={false}
              onChange={(value) => setNewPassword(value)}
              onBlur={() => handleFieldBlur("newPassword")}
            />
            <p className="text-sm text-gray-600 mt-1">
              {t("_change_password._password_rule")}
            </p>
            {validationErrors.newPassword && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.newPassword}
              </p>
            )}
          </div>
        )}

        {renderInput(
          `${t("_change_password._new_password_confirmation")}`,
          <div>
            <PasswordInput
              name="verify_password"
              placeholder={t("_change_password._new_password_confirmation")}
              value={confirmedNewPassword}
              disabled={false}
              onChange={(value) => setConfirmedNewPassword(value)}
              onBlur={() => handleFieldBlur("confirmedNewPassword")}
            />
            {validationErrors.confirmedNewPassword && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.confirmedNewPassword}
              </p>
            )}
          </div>
        )}

        <PrimaryActionButton
          isButton={false}
          label={t("_change_password._change")}
          disabled={isDisabled}
        />
      </form>
    </Container>
  );
};

export default Form;
